import { useState, useEffect } from 'react';

export const NAVIGATE_EVENT = 'app:navigate';

export function navigate(path: string, options?: { replace?: boolean }) {
  if (typeof window === 'undefined') return;
  const current = window.location.pathname + window.location.search + window.location.hash;
  if (current === path) return;
  if (options?.replace) {
    window.history.replaceState(null, '', path);
  } else {
    window.history.pushState(null, '', path);
  }
  window.dispatchEvent(new CustomEvent(NAVIGATE_EVENT, { detail: { path } }));
}

export function parseRoute(path: string) {
  // Matches /applications/adminview/{id} or /applicants/adminview/{id}
  const matchApp = path.match(/^\/(applications|applicants)\/adminview\/([a-zA-Z0-9_-]+)$/);
  if (matchApp) {
    return { type: 'applications_detail' as const, id: matchApp[2] };
  }

  // Matches /myevents/adminview/{id} or /events/adminview/{id}
  const matchEvent = path.match(/^\/(myevents|events)\/adminview\/([a-zA-Z0-9_-]+)$/);
  if (matchEvent) {
    return { type: 'events_detail' as const, id: matchEvent[2] };
  }

  // Matches /sponsors/adminview/{id}
  const matchSponsor = path.match(/^\/sponsors\/adminview\/([a-zA-Z0-9_-]+)$/);
  if (matchSponsor) {
    return { type: 'sponsors_detail' as const, id: matchSponsor[1] };
  }

  // Matches /employers/adminview/{id}
  const matchEmployer = path.match(/^\/employers\/adminview\/([a-zA-Z0-9_-]+)$/);
  if (matchEmployer) {
    return { type: 'employers_detail' as const, id: matchEmployer[1] };
  }

  // Matches /referrers/adminview/{id}
  const matchReferrer = path.match(/^\/referrers\/adminview\/([a-zA-Z0-9_-]+)$/);
  if (matchReferrer) {
    return { type: 'referrers_detail' as const, id: matchReferrer[1] };
  }

  // Matches /programs/adminview/{id}
  const matchProgram = path.match(/^\/programs\/adminview\/([a-zA-Z0-9_-]+)$/);
  if (matchProgram) {
    return { type: 'programs_detail' as const, id: matchProgram[1] };
  }

  // Matches /organizations/adminview/{id}
  const matchOrg = path.match(/^\/organizations\/adminview\/([a-zA-Z0-9_-]+)$/);
  if (matchOrg) {
    return { type: 'organizations_detail' as const, id: matchOrg[1] };
  }

  // Matches /users/adminview/{id}
  const matchUser = path.match(/^\/users\/adminview\/([a-zA-Z0-9_-]+)$/);
  if (matchUser) {
    return { type: 'users_detail' as const, id: matchUser[1] };
  }

  // Matches /tickets/adminview/{id}
  const matchTicket = path.match(/^\/tickets\/adminview\/([a-zA-Z0-9_-]+)$/);
  if (matchTicket) {
    return { type: 'tickets_detail' as const, id: matchTicket[1] };
  }

  // Public routes (magic link booking)
  const matchGroup = path.match(/^\/group-schedule\/([a-zA-Z0-9_-]+)$/);
  if (matchGroup) {
    return { type: 'group_schedule' as const, id: matchGroup[1] };
  }
  const matchSchedule = path.match(/^\/schedule\/([a-zA-Z0-9_-]+)$/);
  if (matchSchedule) {
    return { type: 'schedule' as const, id: matchSchedule[1] };
  }
  const matchSpecial = path.match(/^\/special-request\/([a-zA-Z0-9_-]+)$/);
  if (matchSpecial) {
    return { type: 'special_request' as const, id: matchSpecial[1] };
  }
  const matchPay = path.match(/^\/pay\/([a-zA-Z0-9_-]+)$/);
  if (matchPay) {
    return { type: 'pay' as const, id: matchPay[1] };
  }

  // Matches /admin/workflow or /admin/workflow/s/{stateKey}
  if (path.startsWith('/admin/workflow')) {
    const stateMatch = path.match(/^\/admin\/workflow\/s\/([a-zA-Z0-9_-]+)$/);
    if (stateMatch) {
      return { type: 'workflow_state' as const, stateKey: stateMatch[1], section: 'workflow' };
    }
    return { type: 'base' as const, section: 'workflow' };
  }

  // Base path matching
  const baseSegment = path.split('/')[1] || 'dashboard';
  return { type: 'base' as const, section: baseSegment };
}

export function usePath() {
  const [path, setPath] = useState(() => typeof window !== 'undefined' ? window.location.pathname : '/dashboard');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handlePopState = () => {
      setPath(window.location.pathname);
    };

    const handleNavigate = (e: Event) => {
      const customEvent = e as CustomEvent;
      setPath(new URL(customEvent.detail.path, window.location.origin).pathname);
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener(NAVIGATE_EVENT, handleNavigate);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener(NAVIGATE_EVENT, handleNavigate);
    };
  }, []);

  return path;
}
